/* eslint-disable class-methods-use-this */
/* global axios */
import { stagingUrl } from '../api/automation.stub';
import JwtAuth from './jwtAuth';
import { buildIdempotentHeaders } from 'dashboard/api/helpers/inboxMwHeaders';

const url =
  process.env.FRONTEND_ENV === 'develop'
    ? stagingUrl
    : window.location.protocol + '//' + window.location.hostname;

export const buildMessageTemplatesParams = (
  page,
  sortAttr,
  limit,
  search,
  all = false
) => {
  if (!page && !sortAttr && !limit) {
    return '';
  }

  let query = '?';

  if (all) {
    query += `all=${all}&`;
  }

  if (search) {
    query += `q=${search}&`;
  }

  if (page) {
    query += `page=${page}&`;
  }

  if (sortAttr) {
    query += `sort=${sortAttr}&`;
  }

  if (limit) {
    query += `limit=${limit}`;
  }

  if (query[query.length - 1] === '&') {
    query = query.slice(0, query.length - 1);
  }

  return query;
};

// Helper functions to transform Phoenix response to legacy format
// Exported for reuse in Vuex actions
export const transformPhoenixTemplateToLegacy = (
  phoenixTemplate,
  accountId
) => {
  // Always use template name for _id - the automation-studio expects names, not internal IDs
  const fallbackId = phoenixTemplate.name;

  // Sort translations first so we can use the primary translation for timestamps
  const sortedTranslations = phoenixTemplate.translations
    .map((translation) => ({
      language: translation.language,
      status: translation.status,
      components: translation.components || [],
      // Don't add links property - let Phoenix merge fields be detected by components-only presence
      locked: translation.status === 'approved',
      rejectedReason: translation.rejectedReason || 'NONE',
      statusUpdatedAt: translation.updatedAt,
      _id: translation.id,
      createdAt: translation.createdAt,
      updatedAt: translation.updatedAt,
      sortOrder: translation.sortOrder,
    }))
    .sort((a, b) => {
      // Sort by sortOrder ascending, nulls last
      const aIsNull = a.sortOrder == null;
      const bIsNull = b.sortOrder == null;

      if (aIsNull && bIsNull) return 0;
      if (aIsNull) return 1;
      if (bIsNull) return -1;
      return a.sortOrder - b.sortOrder;
    });

  return {
    _id: fallbackId,
    clientId: phoenixTemplate.accountId || accountId,
    phoneNumber: phoenixTemplate.phoneNumber || '',
    wabaId: phoenixTemplate.wabaId,
    title: phoenixTemplate.name,
    name: phoenixTemplate.name,
    externalName: phoenixTemplate.externalName || null,
    translations: sortedTranslations,
    category: phoenixTemplate.category,
    createdAt: sortedTranslations[0]?.createdAt || new Date().toISOString(),
    updatedAt: sortedTranslations[0]?.updatedAt || new Date().toISOString(),
    __v: 0,
  };
};

const transformPhoenixResponseToLegacy = (phoenixResponse, accountId) => {
  return {
    templates: (phoenixResponse.data || []).map((template) =>
      transformPhoenixTemplateToLegacy(template, accountId)
    ),
    totalPages: phoenixResponse.pagination?.totalPages || 1,
    totalCount:
      phoenixResponse.pagination?.totalItems ||
      phoenixResponse.data?.length ||
      0,
  };
};

const buildPhoenixQueryParams = (accountId, requestParams = {}) => {
  const params = new URLSearchParams();
  params.set('accountId', String(accountId));
  params.set('grouped', 'true');

  const { page, limit, search, status, languages, excludeDeleted } =
    requestParams;

  params.set('page', String(page || 1));

  if (limit) {
    params.set('limit', String(limit));
  }

  if (search) {
    params.set('templateName', search);
  }

  if (status) {
    params.set('status', status);
  }

  if (excludeDeleted != null) {
    params.set('excludeDeleted', String(excludeDeleted));
  }

  if (Array.isArray(languages) && languages.length) {
    params.set('languages', languages.join(','));
  }

  return params;
};

const transformPhoenixSmsTemplateToLegacy = (phoenixSmsTemplate) => {
  const fallbackId = phoenixSmsTemplate.uuid || phoenixSmsTemplate.name;

  return {
    _id: fallbackId,
    clientId: phoenixSmsTemplate.accountId,
    title: phoenixSmsTemplate.name,
    name: phoenixSmsTemplate.name,
    senderName: phoenixSmsTemplate.senderName,
    senderNameUuid: phoenixSmsTemplate.senderNameUuid,
    translations: (phoenixSmsTemplate.translations || [])
      .map((translation) => ({
        components: [{ type: 'BODY', text: translation.content }],
        language: translation.language,
        body: translation.content,
        hasVariables: translation.hasVariables,
        variableMappings: translation.variableMappings,
        _id: translation.uuid,
        createdAt: translation.createdAt,
        updatedAt: translation.updatedAt,
        createdBy: translation.createdBy,
        modifiedBy: translation.modifiedBy,
        sortOrder: translation.sortOrder,
      }))
      .sort((a, b) => {
        // Sort by sortOrder ascending, nulls last
        const aIsNull = a.sortOrder == null;
        const bIsNull = b.sortOrder == null;

        if (aIsNull && bIsNull) return 0;
        if (aIsNull) return 1;
        if (bIsNull) return -1;
        return a.sortOrder - b.sortOrder;
      }),
    createdAt:
      phoenixSmsTemplate.translations?.[0]?.createdAt ||
      new Date().toISOString(),
    updatedAt:
      phoenixSmsTemplate.translations?.[0]?.updatedAt ||
      new Date().toISOString(),
    __v: 0,
  };
};

const transformPhoenixSmsResponseToLegacy = (phoenixResponse) => {
  return {
    smsTemplates: (phoenixResponse.data || []).map(
      transformPhoenixSmsTemplateToLegacy
    ),
    totalPages: phoenixResponse.pagination?.totalPages || 1,
    totalCount:
      phoenixResponse.pagination?.totalItems ||
      phoenixResponse.data?.length ||
      0,
  };
};

const buildPhoenixSmsQueryParams = (accountId, requestParams = {}) => {
  const params = new URLSearchParams();
  params.set('accountId', String(accountId));
  params.set('grouped', 'true');

  const { page, limit, search, senderNameUuid } = requestParams;

  params.set('page', String(page || 1));

  if (limit) {
    params.set('limit', String(limit));
  }

  if (search) {
    params.set('templateName', search);
  }

  if (senderNameUuid) {
    params.set('senderNameUuid', senderNameUuid);
  }

  return params;
};

class MessageTemplateAPI {
  getTemplates = async (accountId, requestParams = {}) => {
    const jwtAuthTokens = await JwtAuth.getUserTokens();

    return axios.get(
      `${url}/templates/template/all/${accountId}${buildMessageTemplatesParams(
        requestParams.page,
        requestParams.sortAttr,
        requestParams.limit,
        requestParams.search
      )}`,
      {
        headers: {
          Authorization: `Bearer ${jwtAuthTokens.access_token}`,
        },
      }
    );
  };

  getAllTemplates = async (accountId, requestParams = {}) => {
    const jwtAuthTokens = await JwtAuth.getUserTokens();

    return axios.get(
      `${url}/templates/template/all/${accountId}${buildMessageTemplatesParams(
        1,
        requestParams.sortAttr,
        requestParams.limit,
        requestParams.search,
        true
      )}`,
      {
        headers: {
          Authorization: `Bearer ${jwtAuthTokens.access_token}`,
        },
      }
    );
  };

  getAllTemplatesName = async (accountId) => {
    const jwtAuthTokens = await JwtAuth.getUserTokens();

    return axios.get(`${url}/templates/template/all/${accountId}/names`, {
      headers: {
        Authorization: `Bearer ${jwtAuthTokens.access_token}`,
      },
    });
  };

  // Phoenix route methods
  getPhoenixTemplates = async (accountId, requestParams = {}) => {
    const jwtAuthTokens = await JwtAuth.getUserTokens();

    const phoenixParams = buildPhoenixQueryParams(accountId, requestParams);

    const response = await axios.get(
      `${url}/inboxmw/whatsapp/templates?${phoenixParams.toString()}`,
      {
        headers: {
          Authorization: `Bearer ${jwtAuthTokens.access_token}`,
        },
      }
    );

    const transformedData = transformPhoenixResponseToLegacy(
      response.data,
      accountId
    );

    return {
      ...response,
      data: transformedData,
    };
  };

  getAllPhoenixTemplates = async (accountId, requestParams = {}) => {
    const jwtAuthTokens = await JwtAuth.getUserTokens();
    const phoenixParams = buildPhoenixQueryParams(accountId, requestParams);

    const response = await axios.get(
      `${url}/inboxmw/whatsapp/templates?${phoenixParams.toString()}`,
      {
        headers: {
          Authorization: `Bearer ${jwtAuthTokens.access_token}`,
        },
      }
    );

    const transformedData = transformPhoenixResponseToLegacy(
      response.data,
      accountId
    );

    return {
      ...response,
      data: transformedData,
    };
  };

  // Phoenix: fetch a specific template by name
  getPhoenixTemplateByName = async (
    accountId,
    templateName,
    requestParams = {}
  ) => {
    const jwtAuthTokens = await JwtAuth.getUserTokens();

    const phoenixParams = buildPhoenixQueryParams(accountId, requestParams);
    phoenixParams.set('templateName', templateName);

    return axios.get(
      `${url}/inboxmw/whatsapp/templates?${phoenixParams.toString()}`,
      {
        headers: {
          Authorization: `Bearer ${jwtAuthTokens.access_token}`,
        },
      }
    );
  };

  getTemplatesCount = async (accountId) => {
    const jwtAuthTokens = await JwtAuth.getUserTokens();

    return axios.get(`${url}/templates/template/all/${accountId}/count`, {
      headers: {
        Authorization: `Bearer ${jwtAuthTokens.access_token}`,
      },
    });
  };

  getTemplate = async (templateId) => {
    const jwtAuthTokens = await JwtAuth.getUserTokens();

    return axios.get(`${url}/templates/template/${templateId}`, {
      headers: {
        Authorization: `Bearer ${jwtAuthTokens.access_token}`,
      },
    });
  };

  getTemplatesByName = async (names) => {
    const jwtAuthTokens = await JwtAuth.getUserTokens();

    return axios.get(`${url}/templates/template/find/by-names?names=${names}`, {
      headers: {
        Authorization: `Bearer ${jwtAuthTokens.access_token}`,
      },
    });
  };

  getPhoenixTemplatesByExternalName = async (accountId, externalNames) => {
    const jwtAuthTokens = await JwtAuth.getUserTokens();
    const externalNamesParam = Array.isArray(externalNames)
      ? externalNames.join(',')
      : externalNames;

    return axios.get(`${url}/inboxmw/whatsapp/templates`, {
      params: {
        accountId,
        externalName: externalNamesParam,
        excludeDeleted: 'false',
        limit: 1,
        grouped: 'true',
      },
      headers: {
        Authorization: `Bearer ${jwtAuthTokens.access_token}`,
      },
    });
  };

  createTemplate = async (accountId, newTemplate) => {
    const jwtAuthTokens = await JwtAuth.getUserTokens();

    return axios.post(
      `${url}/templates/template`,
      { clientId: accountId, ...newTemplate },
      {
        headers: {
          Authorization: `Bearer ${jwtAuthTokens.access_token}`,
        },
      }
    );
  };

  addTemplateTranslation = async (templateId, translation) => {
    const jwtAuthTokens = await JwtAuth.getUserTokens();

    return axios.post(
      `${url}/templates/template/${templateId}/translation`,
      translation,
      {
        headers: {
          Authorization: `Bearer ${jwtAuthTokens.access_token}`,
        },
      }
    );
  };

  updateTemplate = async (templateId, template) => {
    const jwtAuthTokens = await JwtAuth.getUserTokens();

    return axios.patch(`${url}/templates/template/${templateId}`, template, {
      headers: {
        Authorization: `Bearer ${jwtAuthTokens.access_token}`,
      },
    });
  };

  deleteTemplate = async (templateId) => {
    const jwtAuthTokens = await JwtAuth.getUserTokens();

    return axios.delete(`${url}/templates/template/${templateId}`, {
      headers: {
        Authorization: `Bearer ${jwtAuthTokens.access_token}`,
      },
    });
  };

  // Phoenix: delete an approved template by name + (phoneNumber or wabaId)
  deletePhoenixTemplateByName = async (name, { phoneNumber, wabaId } = {}) => {
    const jwtAuthTokens = await JwtAuth.getUserTokens();

    const params = { name };
    if (wabaId) {
      params.wabaId = wabaId;
    } else if (phoneNumber) {
      // Strip a single leading '+' if present
      const sanitized = String(phoneNumber).replace(/^\+/, '');
      params.phoneNumber = sanitized;
    }

    return axios.delete(`${url}/inboxmw/whatsapp/templates`, {
      headers: buildIdempotentHeaders(jwtAuthTokens.access_token),
      params,
    });
  };

  // Phoenix: delete a draft-only template by name + accountId
  deletePhoenixDraftTemplateByName = async (name, accountId) => {
    const jwtAuthTokens = await JwtAuth.getUserTokens();

    return axios.delete(`${url}/inboxmw/whatsapp/templates/drafts`, {
      headers: buildIdempotentHeaders(jwtAuthTokens.access_token),
      params: { name, accountId },
    });
  };

  deleteTemplateTranslation = async (templateId, language) => {
    const jwtAuthTokens = await JwtAuth.getUserTokens();

    return axios.delete(
      `${url}/templates/template/${templateId}/translation/${language}`,
      {
        headers: {
          Authorization: `Bearer ${jwtAuthTokens.access_token}`,
        },
      }
    );
  };

  // For draft templates - uses drafts endpoint with accountId
  deletePhoenixDraftTemplateTranslation = async (translationId, accountId) => {
    const jwtAuthTokens = await JwtAuth.getUserTokens();

    return axios.delete(
      `${url}/inboxmw/whatsapp/templates/drafts/${translationId}`,
      {
        headers: buildIdempotentHeaders(jwtAuthTokens.access_token),
        params: { accountId },
      }
    );
  };

  // For rejected templates - uses main templates endpoint WITH wabaId/phoneNumber
  deletePhoenixRejectedTemplateTranslation = async (
    translationId,
    { wabaId, phoneNumber } = {}
  ) => {
    const jwtAuthTokens = await JwtAuth.getUserTokens();

    const params = {};
    if (wabaId) {
      params.wabaId = wabaId;
    } else if (phoneNumber) {
      params.phoneNumber = String(phoneNumber).replace(/^\+/, '');
    }

    return axios.delete(`${url}/inboxmw/whatsapp/templates/${translationId}`, {
      headers: {
        Authorization: `Bearer ${jwtAuthTokens.access_token}`,
        'Idempotency-Key': getUuid(),
      },
      params,
    });
  };

  getTemplateEntrypoints = async (accountId, templateId) => {
    const jwtAuthTokens = await JwtAuth.getUserTokens();

    return axios.get(
      `${url}/automation/automations/${accountId}/template-entrypoints/${templateId}`,
      {
        headers: {
          Authorization: `Bearer ${jwtAuthTokens.access_token}`,
        },
      }
    );
  };

  // Scheduled templates

  getScheduledTemplates = async (accountId, requestParams = {}) => {
    const jwtAuthTokens = await JwtAuth.getUserTokens();

    return axios.get(
      `${url}/automation/scheduled/${accountId}${buildMessageTemplatesParams(
        requestParams.page,
        requestParams.sortAttr,
        requestParams.limit
      )}`,
      {
        headers: {
          Authorization: `Bearer ${jwtAuthTokens.access_token}`,
        },
      }
    );
  };

  getScheduledTemplate = async (scheduledId) => {
    const jwtAuthTokens = await JwtAuth.getUserTokens();

    return axios.get(`${url}/automation/scheduled/details/${scheduledId}`, {
      headers: {
        Authorization: `Bearer ${jwtAuthTokens.access_token}`,
      },
    });
  };

  cancelScheduledMessageTemplate = async (id) => {
    const jwtAuthTokens = await JwtAuth.getUserTokens();

    return axios.delete(`${url}/automation/scheduled/${id}`, {
      headers: {
        Authorization: `Bearer ${jwtAuthTokens.access_token}`,
      },
    });
  };

  // SMS templates
  getSmsTemplates = async (accountId, requestParams = {}) => {
    const jwtAuthTokens = await JwtAuth.getUserTokens();

    return axios.get(
      `${url}/templates/sms/all/${accountId}${buildMessageTemplatesParams(
        requestParams.page,
        requestParams.sortAttr,
        requestParams.limit,
        requestParams.search
      )}`,
      {
        headers: {
          Authorization: `Bearer ${jwtAuthTokens.access_token}`,
        },
      }
    );
  };

  getAllSmsTemplates = async (accountId, requestParams = {}) => {
    const jwtAuthTokens = await JwtAuth.getUserTokens();

    return axios.get(
      `${url}/templates/sms/all/${accountId}${buildMessageTemplatesParams(
        1,
        requestParams.sortAttr,
        requestParams.limit,
        requestParams.search,
        true
      )}`,
      {
        headers: {
          Authorization: `Bearer ${jwtAuthTokens.access_token}`,
        },
      }
    );
  };

  getAllSmsTemplatesName = async (accountId) => {
    const jwtAuthTokens = await JwtAuth.getUserTokens();

    return axios.get(`${url}/templates/sms/all/${accountId}/names`, {
      headers: {
        Authorization: `Bearer ${jwtAuthTokens.access_token}`,
      },
    });
  };

  getSmsTemplatesCount = async (accountId) => {
    const jwtAuthTokens = await JwtAuth.getUserTokens();

    return axios.get(`${url}/templates/sms/all/${accountId}/count`, {
      headers: {
        Authorization: `Bearer ${jwtAuthTokens.access_token}`,
      },
    });
  };

  getSmsTemplate = async (templateId) => {
    const jwtAuthTokens = await JwtAuth.getUserTokens();

    return axios.get(`${url}/templates/sms/${templateId}`, {
      headers: {
        Authorization: `Bearer ${jwtAuthTokens.access_token}`,
      },
    });
  };

  createSmsTemplate = async (accountId, newTemplate) => {
    const jwtAuthTokens = await JwtAuth.getUserTokens();

    return axios.post(
      `${url}/templates/sms`,
      { clientId: accountId, ...newTemplate },
      {
        headers: {
          Authorization: `Bearer ${jwtAuthTokens.access_token}`,
        },
      }
    );
  };

  addSmsTemplateTranslation = async (templateId, translation) => {
    const jwtAuthTokens = await JwtAuth.getUserTokens();

    return axios.post(
      `${url}/templates/sms/${templateId}/translation`,
      translation,
      {
        headers: {
          Authorization: `Bearer ${jwtAuthTokens.access_token}`,
        },
      }
    );
  };

  updateSmsTemplate = async (templateId, template) => {
    const jwtAuthTokens = await JwtAuth.getUserTokens();

    return axios.patch(`${url}/templates/sms/${templateId}`, template, {
      headers: {
        Authorization: `Bearer ${jwtAuthTokens.access_token}`,
      },
    });
  };

  deleteSmsTemplate = async (templateId) => {
    const jwtAuthTokens = await JwtAuth.getUserTokens();

    return axios.delete(`${url}/templates/sms/${templateId}`, {
      headers: {
        Authorization: `Bearer ${jwtAuthTokens.access_token}`,
      },
    });
  };

  deleteSmsTemplateTranslation = async (templateId, language) => {
    const jwtAuthTokens = await JwtAuth.getUserTokens();

    return axios.delete(
      `${url}/templates/sms/${templateId}/translation/${language}`,
      {
        headers: {
          Authorization: `Bearer ${jwtAuthTokens.access_token}`,
        },
      }
    );
  };

  // Phoenix SMS API methods
  getPhoenixSmsTemplates = async (accountId, requestParams = {}) => {
    const jwtAuthTokens = await JwtAuth.getUserTokens();
    const phoenixParams = buildPhoenixSmsQueryParams(accountId, requestParams);

    const response = await axios.get(
      `${url}/inboxmw/sms/templates?${phoenixParams.toString()}`,
      {
        headers: {
          Authorization: `Bearer ${jwtAuthTokens.access_token}`,
        },
      }
    );

    const transformedData = transformPhoenixSmsResponseToLegacy(response.data);

    return {
      ...response,
      data: transformedData,
    };
  };

  deletePhoenixSmsTemplateByName = async (
    accountId,
    name,
    requestParams = {}
  ) => {
    const jwtAuthTokens = await JwtAuth.getUserTokens();
    const phoenixParams = buildPhoenixSmsQueryParams(accountId, requestParams);

    phoenixParams.set('name', name);

    return axios.delete(
      `${url}/inboxmw/sms/templates?${phoenixParams.toString()}`,
      {
        headers: buildIdempotentHeaders(jwtAuthTokens.access_token),
      }
    );
  };

  deletePhoenixSmsTemplateTranslation = async (accountId, uuid) => {
    const jwtAuthTokens = await JwtAuth.getUserTokens();
    const phoenixParams = buildPhoenixSmsQueryParams(accountId, {});

    return axios.delete(
      `${url}/inboxmw/sms/templates/${uuid}?${phoenixParams.toString()}`,
      {
        headers: buildIdempotentHeaders(jwtAuthTokens.access_token),
      }
    );
  };
}

export default new MessageTemplateAPI();
